import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentUserSafe } from '@/contexts/UserContext';
import { devError, devTime } from '@/lib/debug';
import { toast } from 'sonner';

export type ProfileStatus = 'loading' | 'ready' | 'no-profile' | 'anonymous' | 'error';

interface CurrentProfile {
  id: string;
  wishlist: string[];
  display_name: string | null;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
  preferred_language: string | null;
}

interface ProfileContextValue {
  profile: CurrentProfile | null;
  status: ProfileStatus;
  error: Error | null;
  refresh: () => Promise<void>;
  // Wishlist helpers
  wishlist: string[];
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (productId: string, productTitle?: string) => Promise<void>;
  wishlistLoading: boolean;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

const GUEST_WISHLIST_KEY = 'wishlist';

/**
 * Provider that centralizes profile fetching.
 * Fetches profile ONCE when user is authenticated, and caches it.
 * Handles guest wishlist via localStorage.
 */
export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const userContext = useCurrentUserSafe();
  const [profile, setProfile] = useState<CurrentProfile | null>(null);
  const [status, setStatus] = useState<ProfileStatus>('loading');
  const [error, setError] = useState<Error | null>(null);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  
  const loadedForUserId = useRef<string | null>(null);

  const loadGuestWishlist = useCallback(() => {
    try {
      const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
      setWishlist(stored ? JSON.parse(stored) : []);
    } catch {
      setWishlist([]);
    }
    setWishlistLoading(false);
    setStatus('anonymous');
  }, []);

  const loadProfile = useCallback(async (userId: string) => {
    // Skip if already loaded for this user
    if (loadedForUserId.current === userId) return;
    loadedForUserId.current = userId;

    const endTimer = devTime('ProfileContext Load profile');
    setStatus('loading');
    
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, wishlist, display_name, avatar_url, email, phone, preferred_language')
        .eq('id', userId)
        .maybeSingle();

      endTimer();

      if (profileError) throw profileError;

      if (profileData) {
        setProfile({
          id: profileData.id,
          wishlist: profileData.wishlist || [],
          display_name: profileData.display_name,
          avatar_url: profileData.avatar_url,
          email: profileData.email,
          phone: profileData.phone,
          preferred_language: profileData.preferred_language
        });
        setWishlist(profileData.wishlist || []);
        setStatus('ready');
      } else {
        setProfile(null);
        setWishlist([]);
        setStatus('no-profile');
      }
      setError(null);
    } catch (err) {
      devError('ProfileContext', 'Error loading profile:', err);
      setError(err as Error);
      setStatus('error');
    } finally {
      setWishlistLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!userContext?.user) {
      loadGuestWishlist();
      return;
    }
    loadedForUserId.current = null; // Force reload
    await loadProfile(userContext.user.id);
  }, [userContext?.user, loadProfile, loadGuestWishlist]);

  // React to user changes
  useEffect(() => {
    if (!userContext) {
      // No user context available yet
      setStatus('loading');
      return;
    }

    if (userContext.status === 'loading') {
      setStatus('loading');
      return;
    }

    if (userContext.status === 'anonymous' || !userContext.user) {
      loadGuestWishlist();
      setProfile(null);
      loadedForUserId.current = null;
      return;
    }

    if (userContext.status === 'authenticated' && userContext.user) {
      loadProfile(userContext.user.id);
    }
  }, [userContext?.status, userContext?.user?.id, loadProfile, loadGuestWishlist]);

  const isInWishlist = useCallback((productId: string) => {
    return wishlist.includes(productId);
  }, [wishlist]);

  const toggleWishlist = useCallback(async (productId: string, productTitle?: string) => {
    const isCurrentlyInWishlist = wishlist.includes(productId);
    const newWishlist = isCurrentlyInWishlist
      ? wishlist.filter(id => id !== productId)
      : [...wishlist, productId];

    // Optimistic update
    setWishlist(newWishlist);

    // toast is imported at top of file

    if (!userContext?.user) {
      // Guest: save to localStorage
      localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(newWishlist));
      toast.success(
        isCurrentlyInWishlist ? 'Removed from wishlist' : 'Added to wishlist',
        { description: productTitle, duration: 3000 }
      );
      return;
    }

    // Logged in: save to database
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ wishlist: newWishlist })
        .eq('id', userContext.user.id);

      if (updateError) throw updateError;

      toast.success(
        isCurrentlyInWishlist ? 'Removed from wishlist' : 'Added to wishlist',
        { description: productTitle, duration: 3000 }
      );
    } catch (err) {
      devError('ProfileContext', 'Error updating wishlist:', err);
      // Revert on error
      setWishlist(wishlist);
      toast.error('Could not update wishlist. Please try again.');
    }
  }, [wishlist, userContext?.user]);

  return (
    <ProfileContext.Provider value={{
      profile,
      status,
      error,
      refresh,
      wishlist,
      isInWishlist,
      toggleWishlist,
      wishlistLoading
    }}>
      {children}
    </ProfileContext.Provider>
  );
}

/**
 * Hook to access the current user's profile.
 * ALWAYS use this instead of querying profiles table directly for the current user.
 */
export function useCurrentProfile(): ProfileContextValue {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useCurrentProfile must be used within a ProfileProvider');
  }
  return context;
}

/**
 * Safe version that returns defaults when outside provider
 */
export function useCurrentProfileSafe(): ProfileContextValue | null {
  return useContext(ProfileContext);
}
